import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import AppShell from '@/components/AppShell';
import StarRating from '@/components/StarRating';
import Avatar from '@/components/Avatar';
import { Heart, MapPin, ShieldCheck, Package, ChevronRight, HeartOff } from 'lucide-react';

export default function LikedFarmersPage() {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [farmers, setFarmers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/me/follows');
      setFarmers(data.farmers || []);
    } catch {} finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  return (
    <AppShell
      title={
        <div className="flex items-center gap-2">
          <Heart className="w-5 h-5 text-[#B91C1C] fill-[#B91C1C]" />
          <span>Liked Farmers</span>
        </div>
      }
    >
      <div className="p-4 space-y-3" data-testid="liked-farmers-page">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-20 bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] animate-pulse" />
          ))
        ) : farmers.length === 0 ? (
          <div className="text-center py-16">
            <HeartOff className="w-10 h-10 mx-auto text-[#5B6675]/30 mb-3" />
            <p className="text-sm text-[#5B6675]">You haven't liked any farmers yet.</p>
            <p className="text-xs text-[#5B6675]/70 mt-1">
              Tap the <Heart className="w-3 h-3 inline" /> icon on a farmer's profile to follow them.
            </p>
          </div>
        ) : (
          farmers.map((f, idx) => (
            <button
              key={f.id}
              data-testid={`liked-farmer-${f.id}`}
              onClick={() => navigate(`/farmer/${f.id}`)}
              className="w-full text-left bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-3 flex items-center gap-3 active:scale-[0.99] transition-transform animate-fade-in-up"
              style={{ animationDelay: `${idx * 0.04}s`, opacity: 0 }}
            >
              <Avatar name={f.name} src={f.avatar} size={48} className="border border-[#E4E8EF]" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <p className="text-sm font-semibold text-[#0B1620] truncate">{f.name}</p>
                  {f.is_verified && <ShieldCheck className="w-3 h-3 text-[#15803D] flex-shrink-0" />}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <StarRating value={f.avg_rating} size={11} />
                  <span className="text-[11px] text-[#5B6675]">
                    {f.review_count > 0 ? `${f.avg_rating.toFixed(1)} (${f.review_count})` : 'No ratings'}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-0.5 text-[11px] text-[#5B6675]">
                  <span className="flex items-center gap-0.5"><Package className="w-3 h-3" /> {f.post_count} {f.post_count === 1 ? 'post' : 'posts'}</span>
                  {f.location && <span className="flex items-center gap-0.5 truncate"><MapPin className="w-3 h-3" />{f.location}</span>}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-[#5B6675] flex-shrink-0" />
            </button>
          ))
        )}
      </div>
    </AppShell>
  );
}
